/**
 * HeaderButtonsDetail - Button overlay for Detail pages (articles, events, chats)
 * Layering architecture: sibling to HeaderContainer, both position: fixed
 *
 * USAGE:
 * // CORRECT - siblings, not wrapper:
 * <HeaderContainer />
 * <HeaderButtonsDetail currentContext={currentContext} onBack={handleBack} />
 *
 * REQUIRED PROPS:
 * - currentContext: object with { name: 'Display Name' }
 * - onBack: function to handle back button click
 *
 * FEATURES:
 * Desktop: Back button + title + language selector + profile (80px row)
 * Mobile: Back button + title only (50px row, single row)
 */

import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../../../themes/ThemeProvider';
import useMediaQuery from '../../../hooks/useMediaQuery';
import { useAuth, isSignedIn } from '../../../contexts/AuthContext';
import StandardButton from '../StandardButton';
import ProfileMenu from './ProfileMenu';
import LanguageSelector from '../LanguageSelector';
import MobileHeaderLeftCluster from './MobileHeaderLeftCluster';
import { tokens } from '../../../styles/designTokens';
import { useTranslation } from '../../../i18n/useTranslation';

const HeaderButtonsDetail = ({ currentContext, onBack, onShowLoginModal, title }) => {
    const theme = useTheme();
    const { isMobile, sidebarWidth, isSidebarCompact } = useMediaQuery();
    const { currentUser: authUser, goToLogin } = useAuth();
    // Same "signed in" test as HeaderButtonsFeed (a real user id; the fresh-load guest object is
    // truthy with id: null). Signed out, the identity button shows LogIn and opens the login modal.
    const signedIn = isSignedIn(authUser);
    const { t } = useTranslation('common');
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    // Get context name for display - title prop takes precedence
    const contextName = title || currentContext?.name || '';

    // Calculate Back button position to align right edge with sidebar's right button
    // Sidebar padding: 16px (compact) or 20px (full)
    // Back button width is approximately 100px (icon + gap + text + padding)
    const sidebarPadding = isSidebarCompact ? 16 : 20;
    const backButtonWidth = 100; // Approximate width of "Back" pill button
    const backButtonLeft = sidebarWidth - sidebarPadding - backButtonWidth;

    // Use dynamic theme colors based on current context
    const textColor = theme?.primary || '#024B99';

    // Logo click → back to locality feed (mirrors HeaderContainer's logo, which the opaque mobile
    // detail header now covers).
    const handleLogoClick = () => {
        window.dispatchEvent(new CustomEvent('ournet:context-switch', { detail: { context: 'locality' } }));
    };

    const handleProfileClick = () => {
        console.log('Profile button clicked (Detail)');

        // Signed out: open the login modal (goToLogin, as the feed header does) unless a caller
        // supplies its own handler.
        if (!signedIn) {
            console.log('User not logged in, showing login modal');
            (onShowLoginModal || goToLogin)();
            return;
        }

        // User is logged in, toggle profile menu
        setIsMenuOpen(!isMenuOpen);
    };

    // Mobile: opaque white 50px row — logo + Back pill (shared MobileHeaderLeftCluster, so the logo
    // and pill are dimensionally identical to the feed header's logo + toggle pill) + centered title.
    // Opaque (not a transparent overlay) so it covers HeaderContainer — no double logo, no grey-gap
    // bar — giving the same surface as the feed header. Net: no logo/pill jump on feed↔article.
    if (isMobile) {
        return (
            <header style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                height: `${tokens.mobileHeader.height}px`,
                backgroundColor: 'white',
                zIndex: 101,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                padding: `0 ${tokens.mobileHeader.edgeX}px`,
                boxSizing: 'border-box'
            }}>
                <MobileHeaderLeftCluster
                    pillLabel={t('back')}
                    pillIcon={ArrowLeft}
                    pillColor={textColor}
                    onPillClick={onBack}
                    onLogoClick={handleLogoClick}
                />

                {/* Title - centered in the space after the left cluster */}
                <div style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '0 8px'
                }}>
                    <span style={{
                        color: textColor,
                        fontSize: '15px',
                        fontFamily: 'OpenSauceSans',
                        fontWeight: 700,
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}>
                        {contextName}
                    </span>
                </div>
            </header>
        );
    }

    // Desktop: 80px row with back + title + language + profile
    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '80px',
            zIndex: 101,
            pointerEvents: 'none'
        }}>
            {/* Back Button - right edge aligns with sidebar's "See their other articles" button */}
            <div style={{ position: 'absolute', left: `${backButtonLeft}px`, top: '12px', zIndex: 3, pointerEvents: 'auto' }}>
                <StandardButton
                    variant="context"
                    iconName="ArrowLeft"
                    label={t('back')}
                    onClick={onBack}
                />
            </div>

            {/* Title - centered on content area */}
            <div style={{
                position: 'absolute',
                left: '480px',
                right: '100px',
                top: '4px',
                height: '56px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 3
            }}>
                <div style={{
                    color: textColor,
                    fontSize: '18px',
                    fontFamily: 'OpenSauceSans',
                    fontWeight: '700',
                    textAlign: 'center'
                }}>
                    {contextName}
                </div>
            </div>

            {/* Language Selector - left of Profile */}
            <div style={{ position: 'absolute', right: '80px', top: '18px', zIndex: 3, pointerEvents: 'auto' }}>
                <LanguageSelector />
            </div>

            {/* Profile Button */}
            <div style={{ position: 'absolute', right: '20px', top: '12px', zIndex: 3, pointerEvents: 'auto' }}>
                <StandardButton
                    variant="identity"
                    iconName={signedIn ? 'User' : 'LogIn'}
                    profileImage={authUser?.avatar_url || null}
                    onClick={handleProfileClick}
                    title={signedIn ? '' : t('auth:log_in')}
                />
            </div>

            {/* Profile Menu */}
            <ProfileMenu
                isOpen={isMenuOpen}
                onClose={() => setIsMenuOpen(false)}
                reputation={6.3} // TODO: Get from database
            />
        </div>
    );
};

export default HeaderButtonsDetail;
